import React from 'react';

interface DeploymentHeaderProps {
  siteName: string;
  domain: string;
  progress: number;
  currentStep: number;
  totalSteps: number;
  elapsedTime: number;
  estimatedTime: number;
  status: 'pending' | 'deploying' | 'completed' | 'failed';
}

const DeploymentHeader: React.FC<DeploymentHeaderProps> = ({
  siteName,
  domain,
  progress,
  currentStep,
  totalSteps,
  elapsedTime,
  estimatedTime,
  status,
}) => {
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const remainingTime = Math.max(0, estimatedTime - elapsedTime);

  const getStatusBadge = () => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
            Completed
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-200">
            <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
            Failed
          </span>
        );
      case 'deploying':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
            <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></span>
            Deploying
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
            <span className="w-2 h-2 bg-gray-500 rounded-full mr-2"></span>
            Pending
          </span>
        );
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Deployment Progress</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Step {currentStep} of {totalSteps} • {formatTime(elapsedTime)} elapsed
            </p>
          </div>
          {getStatusBadge()}
        </div>
      </div>

      {/* Progress Section */}
      <div className="px-6 py-5">
        <div className="mb-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-600">Progress</span>
            <span className="text-sm font-bold text-gray-900 dark:text-white">{progress}%</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
            <div
              className={`h-2 rounded-full transition-all duration-500 ${
                status === 'completed'
                  ? 'bg-green-500'
                  : status === 'failed'
                  ? 'bg-red-500'
                  : 'bg-blue-500'
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-4 mt-5 pt-5 border-t border-gray-100 dark:border-gray-700">
          <div>
            <p className="text-xs text-gray-500 mb-1">Time Elapsed</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white font-mono">{formatTime(elapsedTime)}</p>
          </div>
          {status === 'deploying' && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Time Remaining</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white font-mono">~{formatTime(remainingTime)}</p>
            </div>
          )}
          {status === 'completed' && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Total Duration</p>
              <p className="text-lg font-semibold text-green-600 font-mono">{formatTime(elapsedTime)}</p>
            </div>
          )}
          <div>
            <p className="text-xs text-gray-500 mb-1">Current Stage</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">{currentStep}/{totalSteps}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeploymentHeader;